import React from "react";
import { Box, Typography, Divider } from "@mui/material";

// Small helper: "DrgNo" -> "Drg No", "TargetDate" -> "Target Date"
function humanizeKey(key) {
  if (!key) return "";
  return String(key)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\bId\b/g, "ID")
    .trim();
}

function isDateLike(key, value) {
  if (!value) return false;
  const k = String(key).toLowerCase();
  if (!k.includes("date") && !k.includes("dt") && !k.includes("time")) return false;

  // ISO-ish detection
  if (typeof value === "string" && /\d{4}-\d{2}-\d{2}T/.test(value)) return true;
  return false;
}

function formatValue(key, value) {
  if (value === null || value === undefined || value === "") return "-";

  // Boolean display
  if (typeof value === "boolean") return value ? "Yes" : "No";

  // Date display
  if (isDateLike(key, value)) {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString(); // keep it simple; change to date+time if you want
    }
  }

  // Arrays/objects -> readable
  if (Array.isArray(value)) return value.length ? value.join(", ") : "-";
  if (typeof value === "object") return JSON.stringify(value);

  return String(value);
}

const labelTextStyle = {
  fontSize: "13px",
  color: "#433F5E",
  fontFamily: "Outfit",
};

const valueTextStyle = {
  fontSize: "13px",
  color: "#0A0A0C",
  fontFamily: "Outfit",
  fontWeight: 500,
};

function MetaItem({ label, value }) {
  return (
    <Box sx={{ display: "flex", gap: "10px", alignItems: "baseline", minWidth: 0 }}>
      <Typography sx={{ ...labelTextStyle, whiteSpace: "nowrap" }}>{label}:</Typography>
      <Typography sx={{ ...valueTextStyle, minWidth: 0, wordBreak: "break-word" }}>
        {value}
      </Typography>
    </Box>
  );
}

/**
 * Generic WorkflowMetaCard
 *
 * Props:
 * - cardData: API response object
 * - title?: string (e.g. "Workflow Details")
 * - labelMap?: Record<string,string>
 * - hiddenKeys?: string[]
 * - columns?: 1 | 2 | 3 (default 2)
 * - showHeader?: boolean (default true)
 * - StatusBadge: component to render status (pass in your StatusBadge)
 */
function isIdField(key) {
  return /id/i.test(key);
}
export default function WorkflowMetaCard({
  cardData,
  title = "Workflow Details",
  labelMap = {},
  hiddenKeys = [],
  columns = 2,
  showHeader = true,
  StatusBadge,
}) {
  const parsedData = React.useMemo(() => {
    try {
      return cardData?.data ? JSON.parse(cardData.data) : {};
    } catch (e) {
      console.error("Invalid workflow data JSON:", e, cardData?.data);
      return {};
    }
  }, [cardData?.data]);

  const metaEntries = React.useMemo(() => {
    const entries = Object.entries(parsedData || {})
      .filter(([k]) => !isIdField(k)) // 🔥 hide all ID fields
      .filter(([k]) => !hiddenKeys.includes(k)) // optional extra control
      .map(([k, v]) => ({
        key: k,
        label: labelMap[k] || humanizeKey(k),
        value: formatValue(k, v),
      }));

    // Optional: stable ordering (labels alphabetical). Remove if you want API order.
    entries.sort((a, b) => a.label.localeCompare(b.label));
    return entries;
  }, [parsedData, hiddenKeys, labelMap]);

  return (
    <Box
      sx={{
        //border: "1px solid #ECEAF3",
        borderRadius: "14px",
        // p: 2,
        bgcolor: "#FFFFFF",
      }}
    >
      {/* Header: Initiated By + Status */}
      {showHeader && (
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <Typography sx={{ fontFamily: "Outfit", fontWeight: 700, color: "#0A0A0C" }}>
              {title}
            </Typography>
          </Box>
        </Box>
      )}

      <Divider sx={{ my: 1.5 }} />

      {/* Meta grid */}
      {metaEntries.length === 0 ? (
        <Typography sx={{ ...labelTextStyle, fontStyle: "italic" }}>
          No metadata available
        </Typography>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: `repeat(${Math.min(columns, 2)}, minmax(0, 1fr))`,
              md: `repeat(${columns}, minmax(0, 1fr))`,
            },
            gap: "10px 18px",
          }}
        >
          {metaEntries.map((item) => (
            <MetaItem key={item.key} label={item.label} value={item.value} />
          ))}
        </Box>
      )}
    </Box>
  );
}
